import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { UserService } from '@api-client';
import { AuthTokenService, ToastService } from '@client-libs';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';

export const HttpInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const platformId = inject(PLATFORM_ID);
  const authTokenService = inject(AuthTokenService);
  const userService = inject(UserService);

  if (isPlatformServer(platformId)) {
    return next(req);
  }

  // Clone request with Authorization header if token exists
  const accessToken = authTokenService.getAccessToken();
  let request = req;
  if (accessToken) {
    request = req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Token expired - attempt refresh (비로그인 상태도 허용하므로 리다이렉트 안함)
      if (error.status === 401 || error.status === 498) {
        const refreshToken = authTokenService.getRefreshToken();
        if (refreshToken) {
          return from(
            firstValueFrom(
              userService.userControllerRefresh({ body: { refreshToken } })
            )
          ).pipe(
            switchMap(res => {
              if (res.accessToken) {
                authTokenService.setAccessToken(res.accessToken);
                // Retry original request with new token
                const retryReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${res.accessToken}`,
                  },
                });
                return next(retryReq);
              }
              return throwError(() => error);
            }),
            catchError(() => {
              // 토큰 갱신 실패 시 토큰만 정리, 리다이렉트 안함 (비로그인 허용)
              authTokenService.clearTokens();
              return throwError(() => error);
            })
          );
        }
        // 토큰 없으면 정리만 하고 에러 전파 (비로그인 상태 허용)
        authTokenService.clearTokens();
        return throwError(() => error);
      }

      if (error.status === 500 || error.status === 0) {
        toast.error('서버에 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        return throwError(() => error);
      }

      // 인증 관련 에러(401, 498)는 토스트를 띄우지 않음
      if (error.status === 401 || error.status === 498) {
        return throwError(() => error);
      }

      // 토큰 갱신 요청 실패는 토스트를 띄우지 않음
      if (req.url.includes('/user/refresh')) {
        return throwError(() => error);
      }

      if (error.error?.message) {
        toast.error(error.error.message);
      }

      return throwError(() => error);
    })
  );
};
